import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Box, Flex, Text, Checkbox as CoreCheckbox, Grid } from 'core/components';
import IpFilter from 'app/views/serviceProvider/marketIntel/Filter/IpFilter';
import MarketFilter, { MarketFilterForm } from 'app/views/serviceProvider/marketIntel/Filter/MarketFilter';
import { KMI_INSIGHT_TYPES } from 'app/util/constants';
import MultiSlider, { Handle } from 'core/components/MultiSlider';
import { Field, RadioGroup, Checkbox, InputGroup, TextArea } from 'core/form';

const DEFAULT_TYPES = Object.values(KMI_INSIGHT_TYPES).reduce((acc, { value }) => {
  if ([1, 2, 3, 4, 101, 102].includes(value)) {
    acc[`_${value}`] = true;
  } else {
    acc[`_${value}`] = false;
  }
  return acc;
}, {});

function buildInitialTypes(passed) {
  if (passed && typeof passed === 'object') {
    return { ...DEFAULT_TYPES, ...passed };
  }
  return { ...DEFAULT_TYPES };
}

@observer
export default class InsightsExportOptions extends Component {
  static propTypes = {
    disabled: PropTypes.bool,
    filterValues: PropTypes.object,
    onChange: PropTypes.func,
    showMarket: PropTypes.bool,
    showIp: PropTypes.bool,
    form: PropTypes.object,
    widgetize: PropTypes.bool,
    showTitle: PropTypes.bool,
    marketName: PropTypes.string
  };

  static defaultProps = {
    disabled: false,
    filterValues: {},
    onChange: () => {},
    showMarket: true,
    showIp: true,
    form: null,
    widgetize: false,
    showTitle: true,
    marketName: ''
  };

  constructor(props) {
    super(props);
    const { filterValues } = props;
    // eslint-disable-next-line react/state-in-constructor
    this.state = {
      ip: filterValues.ip || 'v4',
      lookback: filterValues.lookback || 1,
      includeGeo: filterValues.includeGeo !== undefined ? filterValues.includeGeo : true,
      market: filterValues.market || 'planet$earth',
      magnitude: Array.isArray(filterValues.magnitude) ? filterValues.magnitude[0] : filterValues.magnitude || 1,
      types: buildInitialTypes(filterValues.types)
    };
  }

  componentDidMount() {
    const { form } = this.props;
    if (!form) {
      this.propagate();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { form } = this.props;
    if (form) {
      return;
    }
    if (prevState !== this.state) {
      this.propagate();
    }
  }

  propagate() {
    const { onChange, form } = this.props;
    if (form) {
      return;
    }
    const { ip, lookback, includeGeo, market, magnitude, types } = this.state;
    onChange({ ip, lookback, includeGeo, market, magnitude: [magnitude], types });
  }

  toggleType = (id) => {
    this.setState((prev) => ({ types: { ...prev.types, [`_${id}`]: !prev.types[`_${id}`] } }));
  };

  renderTypes() {
    const { types } = this.state;
    return (
      <Flex flexWrap="wrap" maxWidth={440}>
        {Object.values(KMI_INSIGHT_TYPES).map(({ value, label }) => (
          <Flex
            key={value}
            width="50%"
            alignItems="center"
            py={1}
            cursor="pointer"
            onClick={() => this.toggleType(value)}
          >
            <CoreCheckbox checked={!!types[`_${value}`]} mr={1} />
            <Text fontSize={11}>{label}</Text>
          </Flex>
        ))}
      </Flex>
    );
  }

  renderLookback() {
    const { lookback } = this.state;
    const options = [
      { value: 1, label: 'past 24h' },
      { value: 7, label: 'past 7 Days' },
      { value: 30, label: 'past 30 Days' }
    ];
    return (
      <RadioGroup
        small
        field={{ value: lookback, options }}
        options={options}
        onChange={(val) => this.setState({ lookback: parseInt(val, 10) })}
        value={lookback}
      />
    );
  }

  renderExportMode() {
    const { disabled, showMarket, showIp } = this.props;
    const { ip, market, magnitude } = this.state;
    return (
      <Box mt={-1} mb={2}>
        <Flex gap={2} alignItems="center" flexWrap="wrap">
          {showIp && (
            <IpFilter
              defaultFilter={{ ip }}
              onChange={(change) => this.setState({ ip: change.ip?.value || change.ip })}
              disabled={disabled}
            />
          )}
          {showMarket && (
            <Flex alignItems="center">
              <MarketFilter fieldValue={market} disabled={disabled} onChange={(m) => this.setState({ market: m.id })} />
            </Flex>
          )}
        </Flex>
        <Box mt={2}>
          <Text fontSize={12} fontWeight={600} mb={1}>
            Lookback range
          </Text>
          {this.renderLookback()}
        </Box>
        <Box my={2}>
          <Text fontSize={12} fontWeight={600} mb={1}>
            Minimum Magnitude
          </Text>
          <Box width={180} px={1}>
            <MultiSlider min={1} max={5} showTrackFill>
              <Handle value={magnitude} intentAfter="primary" onChange={(val) => this.setState({ magnitude: val })} />
            </MultiSlider>
          </Box>
        </Box>
        <Box mt={2}>
          <Text fontSize={12} fontWeight={600} mb={1}>
            Insight Types
          </Text>
          {this.renderTypes()}
        </Box>
      </Box>
    );
  }

  renderFormMode() {
    const { widgetize, showTitle, marketName, form } = this.props;
    if (!form) {
      return null;
    }
    return (
      <Flex flexDirection="column" flex={1} mt={showTitle ? 2 : 1} px={2} alignItems="flex-start">
        {widgetize && (
          <>
            <Field name="widgetTitle" alignSelf="stretch">
              <InputGroup />
            </Field>
            <Field name="asn" py={1} alignSelf="stretch">
              <TextArea rows={2} fill />
            </Field>
          </>
        )}
        <Flex>
          <Field name="includeGeo" showLabel={false} mb="4px">
            <Checkbox
              showBoxLabel={false}
              labelElement={
                <Text fontSize={12} fontWeight={600}>
                  Use Geo Market Filter
                </Text>
              }
              m={0}
            />
          </Field>
          {!widgetize && form.getField('includeGeo').getValue() && (
            <Text small muted pl={1}>
              {`(${marketName})`}
            </Text>
          )}
        </Flex>
        {widgetize && (
          <>
            <Field
              name="market"
              disabled={!form.getField('includeGeo').getValue()}
              showLabel={false}
              onChange={(field, value) => field.setValue(value?.id)}
              mb={0}
            >
              <MarketFilterForm />
            </Field>
            <Field name="ip" pt={2}>
              <RadioGroup />
            </Field>
          </>
        )}
        <Field name="lookback" pt={2}>
          <RadioGroup />
        </Field>
        <Field name="magnitude" ml={1} pt={2} labelProps={{ ml: -1 }}>
          <MultiSlider min={1} max={5} showTrackFill>
            <Handle value={parseInt(form.getField('magnitude').getValue())} intentAfter="primary" />
          </MultiSlider>
        </Field>
        <Box py={1} alignSelf="stretch">
          <Text fontSize={12} fontWeight={600}>
            Insight Types
          </Text>
          <Grid gridTemplateColumns="1fr 1fr" gridAutoFlow="row dense" gridRowGap={0} gridColumnGap="4px" pt={1}>
            {Object.values(KMI_INSIGHT_TYPES).map((type) => (
              <Field name={`types._${type.value}`} key={`type-check-${type.value}`} showLabel={false}>
                <Checkbox />
              </Field>
            ))}
          </Grid>
        </Box>
      </Flex>
    );
  }

  render() {
    const { form, showTitle } = this.props;
    if (form) {
      return (
        <>
          {showTitle && (
            <Text as="div" fontWeight="heavy" large pl={2} mt={2}>
              Configure Insights
            </Text>
          )}
          {this.renderFormMode()}
        </>
      );
    }
    return this.renderExportMode();
  }
}
